// Reference-counted stream subscriptions, kept free of Vue/ActionCable
// imports so Node can unit-test it.
//
// `acquire` opens a subscription for a key (once) and counts a holder;
// `release` drops a holder. When the last holder releases, the close is
// deferred one macrotask so an unmount immediately followed by a remount of
// the same key (async rerender, same-stream page transition) reuses the
// subscription instead of churning the socket. `pin` opens a subscription
// that is never closed.
export const createStreamRefs = ({
  open,
  close,
  defer = (fn) => setTimeout(fn, 0),
  cancel = (t) => clearTimeout(t),
}) => {
  const entries = {};

  const ensure = (key, args) => {
    let entry = entries[key];
    if (!entry) {
      entry = entries[key] = {
        sub: open(...args),
        count: 0,
        pinned: false,
        timer: null,
      };
    }
    if (entry.timer !== null) {
      cancel(entry.timer);
      entry.timer = null;
    }
    return entry;
  };

  return {
    acquire(key, ...args) {
      const entry = ensure(key, args);
      entry.count += 1;
      return entry.sub;
    },
    pin(key, ...args) {
      const entry = ensure(key, args);
      entry.pinned = true;
      return entry.sub;
    },
    release(key) {
      const entry = entries[key];
      if (!entry || entry.count === 0) return;
      entry.count -= 1;
      if (entry.count > 0 || entry.pinned || entry.timer !== null) return;
      entry.timer = defer(() => {
        entry.timer = null;
        if (entry.count === 0 && !entry.pinned && entries[key] === entry) {
          delete entries[key];
          close(entry.sub);
        }
      });
    },
    size() {
      return Object.keys(entries).length;
    },
  };
};
